import type React from "react";
import { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { range } from "lodash";
import { type SectionProps, type WizardActionsProps } from "../components/wizard/wizardNewPropTypes";

interface Props {
  children: (React.ReactElement<SectionProps> | null | false | undefined)[];
  isSaveInProgress: boolean;
  onProgressAsync?: (activeIndex: number, nextIndex: number) => Promise<[string] | []>;
  onRegressAsync?: (activeIndex: number, nextIndex: number) => Promise<void>;
  onFinishAsync?: () => Promise<void>;
  canProceedAsync?: (activeIndex: number, nextIndex: number) => Promise<boolean | unknown>;
  canRecede?: (activeIndex: number, nextIndex: number) => boolean;
  wizardActions?: WizardActionsProps;
  onCancel: () => void;
  onProgress?: (activeIndex: number, nextIndex: number) => void;
  onRegress?: (activeIndex: number, nextIndex: number) => void;
  onFinish?: () => void;
  activeStepIndex?: number;
}
export const useWizard = (props: Props) => {
  const filteredChildren = props.children.filter((item) => !!item);
  const deferredQueueRef = useRef({} as { [key: string]: () => void });
  const isSaveInProgressRef = useRef(props.isSaveInProgress);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    props.wizardActions && props.wizardActions.openWizard();

    return () => {
      props.wizardActions && props.wizardActions.closeWizard();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSaveInProgressRef.current && !props.isSaveInProgress) {
      const actions = Object.values(deferredQueueRef.current);
      actions.forEach((action: () => void) => {
        action();
      });
    }

    isSaveInProgressRef.current = props.isSaveInProgress;
  }, [props.isSaveInProgress]);

  const canProceedCallbackAsync = useCallback(
    (currentIndex: number, nextIndex: number) => {
      return (props.canProceedAsync || (() => Promise.resolve(true)))(currentIndex, nextIndex);
    },
    [props.canProceedAsync],
  );

  const canRecedeCallbackAsync = useCallback(
    (currentIndex: number, nextIndex: number) => {
      return (props.canRecede || (() => Promise.resolve(true)))(currentIndex, nextIndex);
    },
    [props.canRecede],
  );

  const onProgressCallbackAsync = useCallback(
    async (currentIndex: number, nextIndex: number) => {
      const errors = await props.onProgressAsync?.(currentIndex, nextIndex);

      return !(errors && errors.length > 0);
    },
    [props],
  );

  const closeHandler = () => {
    if (props.isSaveInProgress) {
      deferredQueueRef.current[props.onCancel.name] = props.onCancel;
    } else {
      props.onCancel();
    }
  };

  const { onRegress, onRegressAsync, onProgress } = props;

  const onStepSelected = async (nextIndex: number) => {
    if (activeIndex === nextIndex) {
      return;
    }

    if (nextIndex > activeIndex) {
      if (!(await canProceedCallbackAsync(activeIndex, nextIndex))) {
        return;
      }

      const succeed = await onProgressCallbackAsync(activeIndex, nextIndex);
      if (!succeed) {
        return;
      }
      onProgress?.(activeIndex, nextIndex);
    }

    if (nextIndex < activeIndex) {
      if (!(await canRecedeCallbackAsync(activeIndex, nextIndex))) {
        return;
      }
      onRegress?.(activeIndex, nextIndex);
      await onRegressAsync?.(activeIndex, nextIndex);
    }

    setActiveIndex(nextIndex);
  };

  useEffect(() => {
    if (props.activeStepIndex !== undefined && props.activeStepIndex !== activeIndex) {
      setActiveIndex(props.activeStepIndex);
    }
  }, [activeIndex, props.activeStepIndex]);

  const onNext = async () => {
    const nextIndex = activeIndex + 1;

    const canProceed = await canProceedCallbackAsync(activeIndex, nextIndex);
    if (canProceed) {
      const succeed = await onProgressCallbackAsync(activeIndex, nextIndex);
      if (succeed) {
        props.onProgress?.(activeIndex, nextIndex);
        setActiveIndex(nextIndex);
      }
    }
  };

  const onPrev = async () => {
    const newIndex = activeIndex - 1;

    const canRecede = await canRecedeCallbackAsync(activeIndex, newIndex);
    if (canRecede) {
      props.onRegress?.(activeIndex, newIndex);
      await props.onRegressAsync?.(activeIndex, newIndex);
      setActiveIndex(newIndex);
    }
  };

  const onFinish = async () => {
    props.onFinish?.();
    await props.onFinishAsync?.();
  };

  const availableStepIndexes = useMemo(
    () => getAvailableStepIndexes(filteredChildren, activeIndex),
    [filteredChildren, activeIndex],
  );
  const activeStep = filteredChildren[activeIndex];

  return {
    setActiveIndex,
    closeHandler,
    onStepSelected,
    onNext,
    onPrev,
    onFinish,
    availableStepIndexes,
    activeStep,
    filteredChildren,
    activeIndex,
  };
};

const getAvailableStepIndexes = (children: React.ReactElement<SectionProps>[], activeIndex: number) => {
  const activeStep = children[activeIndex];
  const availableIndexes = range(activeIndex + 1);

  // If current step is locked AND required, OR navigation is locked,
  // only show steps up to current one
  if ((activeStep.props.required && activeStep.props.isLocked) || activeStep.props.isLockNavigation) {
    return availableIndexes;
  }

  // Find next required and locked step
  const nextRequiredInvalidStepIndex = children.findIndex(
    (x: any, i: number) => i > activeIndex && x.props.isLocked && x.props.required,
  );

  // Allow navigation up to next locked required step (or end if none found)
  const lastAvailableStepIndex =
    nextRequiredInvalidStepIndex !== -1 ? nextRequiredInvalidStepIndex : children.length - 1;
  return [...availableIndexes, ...range(activeIndex + 1, lastAvailableStepIndex + 1)];
};
